import { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { GoogleMap, Marker, Polyline } from "@react-google-maps/api";
import { toast } from "react-toastify";
import useGoogleMapsLoader from "../../../../Hooks/useGoogleMapsLoader";
import {
  buildSaveRouteStoppagePayload,
  toTimeOnlyValue,
  formatStoppageLatLng,
  parseStoppageDateTime,
  parseStoppageLatLng,
  resolveStoppagePointOnRoute,
  reverseGeocode,
} from "./routeMasterFormUtils";
import RouteMapModalFooter from "./RouteMapModalFooter";
import { SaveRouteStoppage } from "../../../Apis/routeMasterApi";

const isValidDateTimeValue = (value) => Boolean(parseStoppageDateTime(value));
const STOPPAGE_ROUTE_SNAP_MAX_KM = 0.25;

const DEFAULT_CENTER = { lat: 28.613939, lng: 77.209021 };

const mapContainerStyle = { width: "100%", height: "100%" };

const mapOptions = {
  zoomControl: true,
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  clickableIcons: false,
};

const getMarkerIcon = (type) => {
  if (!window.google?.maps) return undefined;

  const icons = {
    start: { fillColor: "#2563eb", scale: 12, strokeWeight: 2 },
    end: { fillColor: "#dc2626", scale: 12, strokeWeight: 2 },
    route: { fillColor: "#ffffff", scale: 2.5, strokeWeight: 1 },
    stoppage: { fillColor: "#16a34a", scale: 8, strokeWeight: 2 },
    existing: { fillColor: "#15803d", scale: 7, strokeWeight: 2 },
  };

  const config = icons[type] ?? icons.route;

  return {
    path: window.google.maps.SymbolPath.CIRCLE,
    fillColor: config.fillColor,
    fillOpacity: 1,
    strokeColor: "#ffffff",
    strokeWeight: config.strokeWeight,
    scale: config.scale,
    labelOrigin: new window.google.maps.Point(0, 0),
  };
};

const getRoutePointLabel = (type) => {
  if (type === "start") {
    return {
      text: "S",
      color: "#ffffff",
      fontSize: "12px",
      fontWeight: "700",
    };
  }

  if (type === "end") {
    return {
      text: "E",
      color: "#ffffff",
      fontSize: "12px",
      fontWeight: "700",
    };
  }

  return undefined;
};

const RouteStoppageMapModal = ({
  show,
  onHide,
  stoppageIndex = 0,
  routeId = 0,
  routePoints = [],
  existingStoppages = [],
  initialStoppage = {},
  onSave,
  saveToApi = false,
}) => {
  const { isLoaded, loadError } = useGoogleMapsLoader();
  const mapRef = useRef(null);
  const [stoppagePoint, setStoppagePoint] = useState(null);
  const [stoppageName, setStoppageName] = useState("");
  const [arrivalTime, setArrivalTime] = useState("");
  const [departureTime, setDepartureTime] = useState("");
  const [latLngText, setLatLngText] = useState("");
  const [isResolvingName, setIsResolvingName] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const applyStoppagePoint = async (point, keepExistingName = false) => {
    if (!point) return;

    setStoppagePoint(point);
    setLatLngText(formatStoppageLatLng(point.lat, point.lng));

    if (keepExistingName && initialStoppage.name?.trim()) {
      return;
    }

    setIsResolvingName(true);
    try {
      const placeName = await reverseGeocode(point.lat, point.lng);
      if (placeName) {
        setStoppageName(placeName);
      }
    } finally {
      setIsResolvingName(false);
    }
  };

  useEffect(() => {
    if (!show) return;

    const latitude = Number(initialStoppage.latitude);
    const longitude = Number(initialStoppage.longitude);
    const hasPoint = !Number.isNaN(latitude) && !Number.isNaN(longitude);

    setStoppageName(initialStoppage.name ?? "");
    setArrivalTime(toTimeOnlyValue(initialStoppage.arrivalTime) || "");
    setDepartureTime(toTimeOnlyValue(initialStoppage.departureTime) || "");

    if (hasPoint) {
      const point = { lat: latitude, lng: longitude };
      setStoppagePoint(point);
      setLatLngText(formatStoppageLatLng(latitude, longitude));
      if (!initialStoppage.name?.trim()) {
        applyStoppagePoint(point, false);
      }
      return;
    }

    setStoppagePoint(null);
    setLatLngText("");
  }, [show, initialStoppage]);

  const mapCenter = useMemo(() => {
    if (stoppagePoint) return stoppagePoint;
    if (routePoints.length) return routePoints[0];
    return DEFAULT_CENTER;
  }, [stoppagePoint, routePoints]);

  useEffect(() => {
    if (!show || !mapRef.current || !window.google?.maps || routePoints.length < 2) {
      return;
    }

    const map = mapRef.current;
    const bounds = new window.google.maps.LatLngBounds();
    routePoints.forEach((point) => bounds.extend(point));
    map.fitBounds(bounds, 48);
  }, [show, routePoints]);

  const handleMapClick = async (event) => {
    const clickPoint = {
      lat: event.latLng.lat(),
      lng: event.latLng.lng(),
    };

    const resolved = resolveStoppagePointOnRoute(
      clickPoint,
      routePoints,
      STOPPAGE_ROUTE_SNAP_MAX_KM
    );

    if (!resolved.ok) {
      toast.info(resolved.message);
      return;
    }

    await applyStoppagePoint(resolved.point, false);
  };

  const handleLatLngChange = (event) => {
    const value = event.target.value;
    setLatLngText(value);

    const parsed = parseStoppageLatLng(value);
    if (!parsed) return;

    const resolved = resolveStoppagePointOnRoute(
      parsed,
      routePoints,
      STOPPAGE_ROUTE_SNAP_MAX_KM
    );

    if (!resolved.ok) {
      toast.info(resolved.message);
      return;
    }

    setStoppagePoint(resolved.point);
    setLatLngText(
      formatStoppageLatLng(resolved.point.lat, resolved.point.lng)
    );
  };

  const handleSave = async () => {
    if (isSaving) return;

    if (!stoppagePoint) {
      toast.error("Please mark a stoppage point on the route.");
      return;
    }

    if (!stoppageName.trim()) {
      toast.error("Stoppage Name is required.");
      return;
    }

    if (!isValidDateTimeValue(arrivalTime)) {
      toast.error("Please select a valid Arrival Date & Time.");
      return;
    }

    if (!isValidDateTimeValue(departureTime)) {
      toast.error("Please select a valid Departure Date & Time.");
      return;
    }

    const nextStoppageId =
      Number(initialStoppage.stoppageId) > 0
        ? Number(initialStoppage.stoppageId)
        : 0;

    const stoppageData = {
      name: stoppageName.trim(),
      stoppageCode: initialStoppage.stoppageCode?.trim() || "",
      locationName: stoppageName.trim(),
      latitude: stoppagePoint.lat.toString(),
      longitude: stoppagePoint.lng.toString(),
      arrivalTime,
      departureTime,
      stoppageId: nextStoppageId,
      sequence: stoppageIndex + 1,
    };

    if (!saveToApi) {
      onSave?.(stoppageData);
      onHide?.();
      return;
    }

    const requestBody = buildSaveRouteStoppagePayload({
      stoppage: stoppageData,
      routeId,
      sequenceNumber: stoppageIndex + 1,
    });

    setIsSaving(true);
    try {
      const response = await SaveRouteStoppage(requestBody);
      const responseData = response?.data ?? response;
      const isSuccess =
        responseData?.success === true ||
        responseData?.success === "true" ||
        response?.status === 200;

      if (!isSuccess && responseData?.success === false) {
        throw new Error(
          responseData?.message || "Unable to save route stoppage."
        );
      }

      const savedStoppage = {
        ...stoppageData,
        stoppageId:
          responseData?.data?.stoppageId ??
          responseData?.stoppageId ??
          stoppageData.stoppageId,
        stoppageCode: requestBody.stoppageCode,
        sequence: requestBody.sequenceNumber,
      };

      onSave?.(savedStoppage);
      toast.success(responseData?.message || "Stoppage saved successfully.");
      onHide?.();
    } catch (error) {
      const data = error?.response?.data;
      const message =
        (typeof data === "string" && data) ||
        data?.message ||
        error?.message ||
        "Unable to save route stoppage.";
      toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      backdrop="static"
      contentClassName="overflow-visible"
      dialogClassName="overflow-visible"
      aria-labelledby="route-stoppage-map-title"
    >
      <Modal.Body style={{ padding: 0 }}>
        <div style={{ position: "relative", height: "52vh", minHeight: 360 }}>
          {!isLoaded && !loadError ? (
            <div className="d-flex align-items-center justify-content-center h-100 text-muted">
              Loading map...
            </div>
          ) : null}

          {loadError ? (
            <div className="d-flex align-items-center justify-content-center h-100 text-danger">
              Unable to load map.
            </div>
          ) : null}

          {isLoaded ? (
            <GoogleMap
              key={show ? `stoppage-map-${stoppageIndex}` : "stoppage-map-hidden"}
              mapContainerStyle={mapContainerStyle}
              center={mapCenter}
              zoom={14}
              options={mapOptions}
              onClick={handleMapClick}
              onLoad={(map) => {
                mapRef.current = map;
                if (routePoints.length > 1) {
                  const bounds = new window.google.maps.LatLngBounds();
                  routePoints.forEach((point) => bounds.extend(point));
                  map.fitBounds(bounds, 48);
                } else {
                  map.panTo(mapCenter);
                }
                window.setTimeout(() => {
                  window.google.maps.event.trigger(map, "resize");
                  if (routePoints.length > 1) {
                    const bounds = new window.google.maps.LatLngBounds();
                    routePoints.forEach((point) => bounds.extend(point));
                    map.fitBounds(bounds, 48);
                  } else {
                    map.panTo(mapCenter);
                  }
                }, 200);
              }}
            >
              {routePoints.length > 1 ? (
                <Polyline
                  path={routePoints}
                  options={{
                    strokeColor: "#16a34a",
                    strokeOpacity: 1,
                    strokeWeight: 3,
                  }}
                />
              ) : null}

              {routePoints.map((point, index) => {
                let markerType = "route";
                if (index === 0) markerType = "start";
                if (index === routePoints.length - 1 && routePoints.length > 1) {
                  markerType = "end";
                }

                return (
                  <Marker
                    key={`route-point-${index}`}
                    position={point}
                    icon={getMarkerIcon(markerType)}
                    label={getRoutePointLabel(markerType)}
                    title={
                      markerType === "start"
                        ? "Start Point (S)"
                        : markerType === "end"
                          ? "End Point (E)"
                          : `Point ${index + 1}`
                    }
                  />
                );
              })}

              {existingStoppages.map((stoppage, index) => (
                <Marker
                  key={`existing-stoppage-${index}`}
                  position={{
                    lat: Number(stoppage.latitude),
                    lng: Number(stoppage.longitude),
                  }}
                  icon={getMarkerIcon("existing")}
                />
              ))}

              {stoppagePoint ? (
                <Marker position={stoppagePoint} icon={getMarkerIcon("stoppage")} />
              ) : null}
            </GoogleMap>
          ) : null}
        </div>

        <div className="p-4">
          <div className="mb-3">
            <label htmlFor="stoppageName" className="form-label mb-1">
              Stoppage Name
            </label>
            <input
              id="stoppageName"
              type="text"
              className="form-control"
              placeholder="Enter stoppage name"
              value={stoppageName}
              onChange={(event) => setStoppageName(event.target.value)}
            />
            {isResolvingName ? (
              <small className="text-muted">Resolving location name...</small>
            ) : null}
          </div>

          <div className="row">
            <div className="col-md-6 mb-3">
              <label htmlFor="arrivalTime" className="form-label mb-1">
                Arrival Time
              </label>
              <input
                id="arrivalTime"
                type="time"
                step="1"
                className="form-control"
                value={arrivalTime}
                onChange={(event) => setArrivalTime(event.target.value)}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label htmlFor="departureTime" className="form-label mb-1">
                Departure Time
              </label>
              <input
                id="departureTime"
                type="time"
                step="1"
                className="form-control"
                value={departureTime}
                onChange={(event) => setDepartureTime(event.target.value)}
              />
            </div>
          </div>

          <div className="mb-0">
            <label htmlFor="stoppageLatLng" className="form-label mb-1">
              Latitude / Longitude
            </label>
            <input
              id="stoppageLatLng"
              type="text"
              className="form-control"
              placeholder="Click on the drawn route to populate coordinates"
              value={latLngText}
              onChange={handleLatLngChange}
            />
          </div>
        </div>
      </Modal.Body>

      <RouteMapModalFooter
        onClose={onHide}
        onSave={handleSave}
        saveLabel={isSaving ? "Saving..." : "Save"}
        saveDisabled={isSaving}
      />
    </Modal>
  );
};

export default RouteStoppageMapModal;
